/**
 * One way to describe a rate's pricing.
 *
 * Rates reach the UI in three shapes — the GraphQL Rate, the GraphQL Rate
 * nested under a Location, and the legacy snake_case REST row — and each used
 * to carry its own string builder that disagreed with the others. They all go
 * through here now, so a rate reads the same on the Billing tab, on a
 * location's rates card and in the station rate pickers, and matches the
 * preview the add form shows while the rate is being created.
 *
 * This module is deliberately dependency-free apart from constants: utils.jsx
 * imports it, so it must not import utils.jsx back.
 */

import { RATE_DURATIONS } from '../constants';

export interface RatePricing {
  baseRate?: number | null;
  pricePerPeriod?: number | null;
  periodDuration?: number | null;
  freeTime?: number | null;
  /** 1 means the rate charges once for the whole period rather than repeating. */
  maxPeriods?: number | null;
}

/**
 * Spell out a span of seconds, e.g. 5400 -> "1 hour 30 minutes". Mirrors
 * `timeForHumans` in utils.jsx, which cannot be imported here.
 */
export function humanizeSeconds(seconds: number): string {
  if (!seconds || Number.isNaN(seconds)) return '';

  const levels: [number, string][] = [
    [Math.floor(seconds / 31536000), 'years'],
    [Math.floor((seconds % 31536000) / 86400), 'days'],
    [Math.floor(((seconds % 31536000) % 86400) / 3600), 'hours'],
    [Math.floor((((seconds % 31536000) % 86400) % 3600) / 60), 'minutes'],
    [(((seconds % 31536000) % 86400) % 3600) % 60, 'seconds'],
  ];

  return levels
    .filter(([amount]) => amount !== 0)
    .map(([amount, unit]) => `${amount} ${amount === 1 ? unit.slice(0, -1) : unit}`)
    .join(' ');
}

/**
 * Label a rate period, which the API stores in seconds, the way the add form's
 * dropdown labels it. Durations the dropdown doesn't offer are spelled out.
 */
export function formatRatePeriod(seconds: number): string {
  const named = RATE_DURATIONS.find(
    (duration: { seconds: number; value: string }) => duration.seconds === seconds
  );
  return named ? named.value : humanizeSeconds(seconds);
}

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/**
 * Build the sentence that describes what a rate charges.
 */
export function buildRateOverview(rate: RatePricing): string {
  const baseRate = rate.baseRate ?? 0;
  const pricePerPeriod = rate.pricePerPeriod ?? 0;
  const freeTime = rate.freeTime ?? 0;
  const period = formatRatePeriod(rate.periodDuration ?? 0);

  // A fixed rate charges once for the whole period instead of repeating.
  if (rate.maxPeriods === 1) {
    return pricePerPeriod ? `${money(pricePerPeriod)} for ${period}` : 'Free';
  }

  if (!baseRate && !pricePerPeriod) {
    return freeTime ? `${humanizeSeconds(freeTime)} free` : 'Free';
  }

  const freePrefix = freeTime ? `${humanizeSeconds(freeTime)} free, then ` : '';
  const basePrefix = baseRate ? `${money(baseRate)} + ` : '';

  return `${freePrefix}${basePrefix}${money(pricePerPeriod)} per ${period}`;
}

/**
 * Same sentence, from the legacy snake_case rate row. Callers historically
 * spread in a `base` alias for `base_rate`, so both are accepted.
 */
export function buildLegacyRateOverview(rate: any): string {
  if (!rate) return '';

  return buildRateOverview({
    baseRate: rate.base ?? rate.base_rate,
    pricePerPeriod: rate.price_per_period,
    periodDuration: rate.period_duration,
    freeTime: rate.free_time,
    maxPeriods: rate.max_periods,
  });
}
